import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule, ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  INTÉZMÉNYTÍPUS ÉS DÍSZPÉLDÁNY-DÍJ (backend/bizonyitvany-tipus.js, diszpeldany-dij.js).
 *
 *  Az iskolatípusokból dől el, melyik évfolyamon digitális a bizonyítvány (Nkt. 99/M. § felmenő
 *  rendszer; szakképzőben Szkt. 63. § (3), 128/C. §: a 2025/26-tól kezdőknek minden évfolyamon), hogy
 *  szakképzőben a szakképzési sablon és az ágazati alapvizsga-bejegyzés kerül a bizonyítványba, és hogy
 *  a díszpéldány második példányának díja melyik jogszabály szerint jár. A díj felülírható.
 */
@Component({
  selector: 'app-intezmenytipus-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  template: `
<ion-header>
  <ion-toolbar>
    <ion-title>Intézménytípus és díszpéldány-díj</ion-title>
    <ion-buttons slot="end"><ion-button (click)="bezar()">Bezárás</ion-button></ion-buttons>
  </ion-toolbar>
</ion-header>
<ion-content class="ion-padding">
  <div *ngIf="betolt" class="ion-text-center"><ion-spinner name="crescent"></ion-spinner></div>
  <ng-container *ngIf="adat && !betolt">
    <h3>Iskolatípusok ezen a feladatellátási helyen</h3>
    <p class="halvany">
      Ebből dől el, melyik évfolyamon jár digitális bizonyítvány: köznevelési iskolában a 2025/26-os tanévtől
      felmenő rendszerben (1. évfolyam, 4 évfolyamos gimnázium 9., 6 évfolyamos 7., 8 évfolyamos 5. évfolyam –
      Nkt. 99/M. §), szakképző intézményben a 2025/26-tól tanulmányaikat kezdőknek minden évfolyamon
      (Szkt. 63. § (3), 128/C. §). Szakképző intézménynél a bizonyítvány a szakképzési sablonnal, az ágazati
      alapvizsga bejegyzésével készül.
    </p>
    <ion-list lines="none">
      <ion-item *ngFor="let t of adat.tipusok">
        <ion-checkbox labelPlacement="end" justify="start" [checked]="kivalasztva.has(t.kod)" (ionChange)="valt(t.kod, $event.detail.checked)">
          {{ t.nev }}
        </ion-checkbox>
      </ion-item>
    </ion-list>
    <p class="figyel" *ngIf="!kivalasztva.size">Nincs beállítva: a Papír bizonyítvány › Évfolyam-szabály intézményi képzése, annak híján az évfolyam dönt (1–8. általános iskola, 9–12. gimnázium).</p>
    <p class="halvany">Osztályonként a Papír bizonyítvány › Évfolyam-szabály fülön adott képzés (osztály, betűjel, intézményi alap) ennél erősebb – a digitális/papír döntés egyetlen helyen, ugyanígy számolódik.</p>
    <p class="halvany" *ngIf="szakkepzo()"><b>Szakképző intézmény.</b></p>

    <h3>A díszpéldány második és további példányának díja</h3>
    <p class="halvany">
      Ugyanarról a bizonyítványról az első díszpéldány ingyenes, a második és minden további térítéses; a díj
      a kiállításkor a család egyenlegébe kerül (Pénzügy › Egyenleg). A jogszabály szerint
      <b>{{ adat.torvenyi_dij?.osszeg | number:'1.0-0' }} Ft</b> ({{ adat.torvenyi_dij?.jogalap }}).
      Üresen hagyva ez az összeg érvényes (a típus módosítása után a mentés frissíti).
    </p>
    <ion-item lines="full">
      <ion-input type="number" min="0" label="Díj (Ft) – üresen: a jogszabály szerinti" label-placement="stacked"
                 [(ngModel)]="dij" [placeholder]="'' + (adat.torvenyi_dij?.osszeg ?? '')"></ion-input>
    </ion-item>

    <div class="gombok">
      <ion-button (click)="ment()" [disabled]="fut">{{ fut ? 'Mentés…' : 'Mentés' }}</ion-button>
    </div>
  </ng-container>
</ion-content>`,
  styles: [`
    h3 { font-size: 1rem; font-weight: 600; margin: 18px 0 6px; }
    .halvany { color: var(--ion-color-medium); font-size: 0.85rem; }
    .figyel { color: var(--ion-color-warning-shade); font-size: 0.85rem; }
    .gombok { display: flex; justify-content: flex-end; gap: 6px; margin-top: 12px; }
  `],
})
export class IntezmenytipusModalComponent implements OnInit {
  @Input() intezmeny = '';
  adat: any = null;
  betolt = false;
  fut = false;
  kivalasztva = new Set<string>();
  dij: number | string | null = null;

  constructor(private http: HttpClient, private data: DataService, private modal: ModalController) {}

  ngOnInit() { this.toltes(); }
  bezar() { this.modal.dismiss(); }

  async toltes() {
    this.betolt = true;
    try {
      this.adat = await firstValueFrom(this.http.get(`/api/bizonyitvany-tipus/beallitas/${this.intezmeny}`));
      this.kivalasztva = new Set(this.adat.iskolatipus || []);
      this.dij = this.adat.diszpeldany_dij ?? null;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni az intézménytípust.', 'error');
    } finally { this.betolt = false; }
  }

  valt(kod: string, be: boolean) {
    if (be) this.kivalasztva.add(kod); else this.kivalasztva.delete(kod);
  }
  szakkepzo(): boolean {
    return (this.adat?.tipusok || []).some((t: any) => t.szakkepzo && this.kivalasztva.has(t.kod));
  }
  async ment() {
    this.fut = true;
    try {
      const ures = this.dij === null || this.dij === undefined || String(this.dij).trim() === '';
      await firstValueFrom(this.http.post(`/api/bizonyitvany-tipus/beallitas/${this.intezmeny}`, {
        iskolatipus: [...this.kivalasztva],
        diszpeldany_dij: ures ? null : Number(this.dij),
      }));
      this.data.sendToast('Intézménytípus és díszpéldány-díj mentve.', 'success');
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally { this.fut = false; }
  }
}
